import { Component, computed, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Router, RouterLink } from '@angular/router';
import { CompanyStatusComponent } from '@buspro/web/admin/shared';
import { RegisterTenantModalComponent } from '@buspro/web/admin/tenants/shared';
import { CompaniesApi, CompanyInListDto } from '@buspro/web/apis';
import { AppService, CacheKeysEnum } from '@buspro/web/core';
import { injectMutation, injectQuery } from '@tanstack/angular-query-experimental';
import { NzEmptyComponent } from 'ng-zorro-antd/empty';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzPageHeaderModule } from 'ng-zorro-antd/page-header';
import { NzResultComponent } from 'ng-zorro-antd/result';
import { NzSkeletonComponent } from 'ng-zorro-antd/skeleton';
import { AvatarModule } from 'ngx-avatars';
import { NgxLoadingModule } from 'ngx-loading';
import { MomentModule } from 'ngx-moment';
import { lastValueFrom } from 'rxjs';

@Component({
  selector: 'buspro-admin-widgets',
  standalone: true,
  imports: [
    MatIconModule,
    NzPageHeaderModule,
    NzEmptyComponent,
    NzIconModule,
    MatButtonModule,
    MatSnackBarModule,
    AvatarModule,
    CompanyStatusComponent,
    NgxLoadingModule,
    RegisterTenantModalComponent,
    NzSkeletonComponent,
    NzResultComponent,
    MomentModule,
    RouterLink,
    NzInputModule,
  ],
  template: `
    <section class="bg-white p-8 min-h-full">
      <nz-page-header [nzTitle]="title">
        <ng-template #title>
          <p class="text-3xl font-bold">Tenants Directory</p>
        </ng-template>
        <nz-page-header-extra>
          <div class="flex items-center gap-3">
            <button
              (click)="onRefresh()"
              class="btn btn-outline"
              [disabled]="query.isFetching()"
              title="Refresh tenants list"
            >
              @if (query.isFetching()) {
                <span class="loading loading-spinner loading-sm"></span>
              } @else {
                <mat-icon>refresh</mat-icon>
              }
              Refresh
            </button>
            <button (click)="showRegisterTenantModal()" class="btn btn-primary">
              <mat-icon>add_domain</mat-icon>
              Register New Business
            </button>
          </div>
        </nz-page-header-extra>
      </nz-page-header>

      <!-- Search Bar - Only show when there are tenants -->
      @if (query.isFetched() && query.data() && query.data()?.length) {
      <div class="mb-6 mt-4">
        <div class="relative max-w-md">
          <span
            nz-icon
            nzType="search"
            nzTheme="outline"
            class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
          ></span>
          <input
            nz-input
            type="text"
            placeholder="Search tenants by name, admin, email, phone, or country..."
            [value]="searchTerm()"
            (input)="onSearchChange($any($event.target).value)"
            class="pl-10 pr-10 w-full"
            autocomplete="off"
          />
          @if (searchTerm()) {
            <button
              type="button"
              (click)="clearSearch()"
              class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              title="Clear search"
            >
              <span nz-icon nzType="close-circle" nzTheme="outline"></span>
            </button>
          }
        </div>
        @if (searchTerm()) {
          <div class="mt-2 text-sm text-gray-600">
            Showing {{ filteredTenants().length }} of {{ allTenants.length }} tenants
          </div>
        }
      </div>
      }

      @if (query.isError()) {
      <nz-result
        nzStatus="500"
        nzTitle="500"
        nzSubTitle="Sorry, there is an error on the server."
      >
        <div nz-result-extra>
          <div>
            If problem persist, kindly contact technical support for assistance.
          </div>
        </div>
      </nz-result>
      }

      <ngx-loading
        [show]="query.isLoading() && !query.isInitialLoading"
        [config]="{ backdropBackgroundColour: '#ccc' }"
      ></ngx-loading>

      @if (!query.isFetched && query.isInitialLoading() && query.isLoading()) {
      <nz-skeleton
        [nzActive]="true"
        [nzParagraph]="true"
        [nzRound]="true"
      ></nz-skeleton>
      <nz-skeleton
        [nzActive]="true"
        [nzParagraph]="true"
        [nzRound]="true"
      ></nz-skeleton>
      <nz-skeleton
        [nzActive]="true"
        [nzParagraph]="true"
        [nzRound]="true"
      ></nz-skeleton>
      <nz-skeleton
        [nzActive]="true"
        [nzParagraph]="true"
        [nzRound]="true"
      ></nz-skeleton>
      } @if (query.isFetched() && query.data() && query.data()?.length) {
      <!-- Modern Table Container -->
      <div class="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <!-- Table Header -->
        <div class="bg-gradient-to-r from-gray-50 to-gray-100/50 border-b border-gray-200 px-6 py-4">
          <div class="grid grid-cols-12 gap-4 items-center">
            <div class="col-span-3">
              <span class="text-sm font-semibold text-gray-700 uppercase tracking-wide">Company</span>
            </div>
            <div class="col-span-3">
              <span class="text-sm font-semibold text-gray-700 uppercase tracking-wide">Admin Info</span>
            </div>
            <div class="col-span-2 text-center">
              <span class="text-sm font-semibold text-gray-700 uppercase tracking-wide">Country</span>
            </div>
            <div class="col-span-2 text-center">
              <span class="text-sm font-semibold text-gray-700 uppercase tracking-wide">Status</span>
            </div>
            <div class="col-span-2 text-right">
              <span class="text-sm font-semibold text-gray-700 uppercase tracking-wide">Actions</span>
            </div>
          </div>
        </div>

        <!-- Table Body -->
        <div class="divide-y divide-gray-100">
          @for (data of filteredTenants(); track trackByTenantId($index, data)) {
          <div
            class="group relative px-6 py-5 transition-all duration-300 hover:bg-gradient-to-r hover:from-blue-50/50 hover:to-indigo-50/30 hover:shadow-sm"
          >
            <div class="grid grid-cols-12 gap-4 items-center">
              <!-- Company Name Column -->
              <div class="col-span-3">
                <div class="flex items-center space-x-4">
                  <div class="relative flex-shrink-0">
                    <ngx-avatars
                      size="56"
                      [src]="data.logoUrl"
                      class="ring-2 ring-gray-200 group-hover:ring-blue-300 transition-all duration-300"
                    ></ngx-avatars>
                    <!-- Status indicator dot - conditional based on status -->
                    @if (data.status === 'Active') {
                      <div
                        class="absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white bg-green-500"
                        title="Active tenant"
                      ></div>
                    } @else if (data.status === 'Pending') {
                      <div
                        class="absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white bg-yellow-500"
                        title="Pending activation"
                      ></div>
                    } @else if (data.status === 'Suspended') {
                      <div
                        class="absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white bg-red-500"
                        title="Suspended tenant"
                      ></div>
                    } @else {
                      <!-- Fallback for unknown status -->
                      <div
                        class="absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white bg-gray-400"
                        [title]="'Status: ' + data.status"
                      ></div>
                    }
                  </div>
                  <div class="flex-1 min-w-0">
                    <div
                      class="font-semibold text-gray-900 text-base mb-1 group-hover:text-blue-600 transition-colors duration-200"
                    >
                      {{ data.name }}
                    </div>
                    <div class="text-xs text-gray-500 flex items-center">
                      <mat-icon class="text-xs mr-1" style="font-size: 14px; width: 14px; height: 14px"
                        >schedule</mat-icon
                      >
                      Joined {{ data.createdAt | amTimeAgo }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Admin Info Column -->
              <div class="col-span-3">
                @if (data.admin) {
                <div class="space-y-1.5">
                  <div class="font-medium text-gray-900">
                    @if (data.admin.firstName && data.admin.surname) {
                    <span class="flex items-center">
                      <mat-icon class="text-sm mr-1.5 text-gray-400" style="font-size: 16px; width: 16px; height: 16px"
                        >person</mat-icon
                      >
                      {{ data.admin.firstName + ' ' + data.admin.surname }}
                    </span>
                    } @else {
                    <div class="skeleton w-32 h-4"></div>
                    }
                  </div>
                  <div class="flex flex-col space-y-1 text-sm">
                    <a
                      [href]="'mailto:' + data.admin.email"
                      class="text-gray-600 hover:text-blue-600 transition-colors duration-200 flex items-center group/link"
                    >
                      <mat-icon
                        class="text-sm mr-1.5 text-gray-400 group-hover/link:text-blue-500"
                        style="font-size: 14px; width: 14px; height: 14px"
                        >alternate_email</mat-icon
                      >
                      <span class="truncate">{{ data.admin.email }}</span>
                    </a>
                    <a
                      [href]="'tel:' + data.admin.phoneNumber"
                      class="text-gray-600 hover:text-blue-600 transition-colors duration-200 flex items-center group/link"
                    >
                      <mat-icon
                        class="text-sm mr-1.5 text-gray-400 group-hover/link:text-blue-500"
                        style="font-size: 14px; width: 14px; height: 14px"
                        >call</mat-icon
                      >
                      <span>{{ data.admin.phoneNumber }}</span>
                    </a>
                  </div>
                </div>
                } @else {
                <div class="text-gray-400 text-sm italic">No admin assigned</div>
                }
              </div>

              <!-- Country Column -->
              <div class="col-span-2">
                <div class="flex items-center justify-center">
                  <span class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700 text-sm font-medium">
                    <mat-icon class="text-base" style="font-size: 16px; width: 16px; height: 16px">public</mat-icon>
                    {{ data.country || 'N/A' }}
                  </span>
                </div>
              </div>

              <!-- Status Column -->
              <div class="col-span-2">
                <div class="flex items-center justify-center gap-2 flex-wrap">
                  <buspro-admin-company-status [status]="data.status"></buspro-admin-company-status>

                  @if (data.status === 'Pending') {
                  <button
                    type="button"
                    class="btn btn-xs btn-outline btn-warning transition-all duration-200 hover:scale-105 hover:shadow-sm"
                    [disabled]="resendActivationMutation.isPending() && resendingTenantId === data.id"
                    (click)="onResendActivationEmail(data, $event)"
                    title="Resend activation email to admin"
                  >
                    @if (resendActivationMutation.isPending() && resendingTenantId === data.id) {
                    <span class="loading loading-spinner loading-xs"></span>
                    <span class="ml-1">Sending...</span>
                    } @else {
                    <mat-icon class="text-xs" style="font-size: 12px; width: 12px; height: 12px">mail</mat-icon>
                    <span class="ml-1">Resend</span>
                    }
                  </button>
                  }
                </div>
              </div>

              <!-- Actions Column -->
              <div class="col-span-2">
                <div class="flex items-center justify-end space-x-2">
                  <button
                    class="btn btn-sm btn-outline transition-all duration-200 hover:scale-105 hover:shadow-md hover:bg-blue-50 hover:border-blue-300 group/btn"
                    (click)="onTenantSelect(data, $event)"
                  >
                    <mat-icon
                      class="group-hover/btn:text-blue-600 transition-colors duration-200"
                      style="font-size: 18px; width: 18px; height: 18px"
                      >view_compact_alt</mat-icon
                    >
                    <span class="ml-1.5">View Profile</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
          } @empty {
          <div class="flex flex-col items-center justify-center py-20 px-8">
            <div class="max-w-md w-full text-center">
              <div class="mb-6">
                <div
                  class="w-24 h-24 mx-auto bg-gradient-to-br from-blue-50 to-indigo-100 rounded-3xl flex items-center justify-center shadow-lg border border-blue-200 hover:shadow-xl hover:scale-105 transition-all duration-300"
                  style="border-opacity: 0.5"
                >
                  <span
                    nz-icon
                    nzType="appstore"
                    nzTheme="outline"
                    class="text-4xl text-blue-500"
                    role="img"
                    aria-label="Empty tenants icon"
                  ></span>
                </div>
              </div>
              <h3 class="text-2xl font-bold text-gray-900 mb-3">
                @if (searchTerm()) {
                No Matching Tenants
                } @else {
                No Tenants Found
                }
              </h3>
              <p class="text-gray-600 mb-6 leading-relaxed">
                @if (searchTerm()) {
                No tenants match your search "{{ searchTerm() }}". Try adjusting your search terms or clear the
                search to see all tenants.
                } @else {
                No tenants match the current criteria. Try adjusting your filters or check back later.
                }
              </p>
              @if (searchTerm()) {
              <button (click)="clearSearch()" class="btn btn-outline btn-sm">Clear Search</button>
              }
            </div>
          </div>
          }
        </div>
      </div>
      } @else {
      <div class="flex flex-col items-center justify-center py-20 px-8">
        <div class="max-w-md w-full text-center">
          <!-- Modern Empty State Icon -->
          <div class="mb-6">
            <div class="w-28 h-28 mx-auto rounded-3xl flex items-center justify-center shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300" style="background: linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%);">
              <span
                nz-icon
                nzType="appstore"
                nzTheme="outline"
                class="text-5xl text-primary"
                role="img"
                aria-label="Empty tenants directory icon"
              ></span>
            </div>
          </div>

          <!-- Title -->
          <h2 class="text-2xl font-bold text-gray-900 mb-3">
            No Tenants Yet
          </h2>

          <!-- Description -->
          <p class="text-base text-gray-600 max-w-md mx-auto">
            Get started by registering your first business tenant using the button above.
          </p>
        </div>
      </div>
      }

      <buspro-admin-register-tenant-modal
        [isVisible]="isVisible()"
        (cancelled)="isVisible.set(false); query.refetch()"
      ></buspro-admin-register-tenant-modal>
    </section>
  `,
  styles: `
    /* Smooth transitions for table rows */
    .group {
      position: relative;
    }

    /* Add subtle border on hover */
    .group::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 3px;
      background: linear-gradient(to bottom, #3b82f6, #6366f1);
      opacity: 0;
      transition: opacity 0.3s ease;
    }

    .group:hover::before {
      opacity: 1;
    }

    /* Smooth avatar transition */
    ngx-avatars {
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    }

    /* Button hover effects */
    .btn {
      transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    }

    /* Link hover effects */
    a {
      transition: color 0.2s ease;
    }
  `,
})
export class TenantsComponent {
  appService = inject(AppService);
  private companiesApi = inject(CompaniesApi);
  private router = inject(Router);
  private snackBar = inject(MatSnackBar);

  query = injectQuery(() => ({
    queryKey: [CacheKeysEnum.TENANTS],
    queryFn: () => lastValueFrom(this.companiesApi.companyControllerGetCompanies()),
    refetchOnMount: true,
    refetchInterval: 30000, // Refetch every 30 seconds in the background
    refetchIntervalInBackground: true, // Continue refetching even when tab is not active
  }));

  resendActivationMutation = injectMutation(() => ({
    mutationFn: (companyId: string) =>
      lastValueFrom(this.companiesApi.companyControllerResendTenantActivationEmail({ id: companyId })),
    onSuccess: (data) => {
      this.resendingTenantId = null;
      this.snackBar.open(data.message, 'Close', {
        duration: 5000,
        horizontalPosition: 'end',
        verticalPosition: 'top',
      });
    },
    onError: (error: any) => {
      this.resendingTenantId = null;
      console.error('Error resending activation email:', error);

      let errorMessage = '❌ Failed to resend activation email. Please try again.';

      // Handle specific error cases
      if (error?.error?.detail) {
        errorMessage = `❌ ${error.error.detail}`;
      } else if (error?.message) {
        errorMessage = `❌ ${error.message}`;
      }

      this.snackBar.open(errorMessage, 'Close', {
        duration: 8000,
        horizontalPosition: 'end',
        verticalPosition: 'top',
      });
    },
  }));

  // Search functionality
  searchTerm = signal('');

  // Get all tenants from query
  get allTenants(): CompanyInListDto[] {
    return this.query.data() ?? [];
  }

  // Filtered tenants based on search term
  filteredTenants = computed(() => {
    const search = this.searchTerm().toLowerCase().trim();
    if (!search) {
      return this.allTenants;
    }

    return this.allTenants.filter((tenant) => {
      // Search in company name
      const nameMatch = tenant.name?.toLowerCase().includes(search);

      // Search in admin information
      const adminNameMatch =
        tenant.admin?.firstName?.toLowerCase().includes(search) ||
        tenant.admin?.surname?.toLowerCase().includes(search) ||
        `${tenant.admin?.firstName} ${tenant.admin?.surname}`.toLowerCase().includes(search);

      // Search in admin email
      const emailMatch = tenant.admin?.email?.toLowerCase().includes(search);

      // Search in admin phone number
      const phoneMatch = tenant.admin?.phoneNumber?.toLowerCase().includes(search);

      // Search in country
      const countryMatch = tenant.country?.toLowerCase().includes(search);

      // Search in status
      const statusMatch = tenant.status?.toLowerCase().includes(search);

      return nameMatch || adminNameMatch || emailMatch || phoneMatch || countryMatch || statusMatch;
    });
  });

  isVisible = signal(false);
  resendingTenantId: string | null = null;
  // Track previous tenant IDs to prevent flickering during updates
  private previousTenantIds = new Set<string>();

  /**
   * Track tenant by ID to prevent UI flickering during updates
   */
  trackByTenantId(index: number, tenant: CompanyInListDto): string {
    // Store the ID for comparison
    if (tenant.id) {
      this.previousTenantIds.add(tenant.id);
    }
    return tenant.id || `tenant-${index}`;
  }

  /**
   * Handle manual refresh button click
   */
  onRefresh() {
    this.query.refetch();
  }

  /**
   * Handle search input changes
   */
  onSearchChange(value: string) {
    this.searchTerm.set(value);
  }

  /**
   * Clear search input
   */
  clearSearch() {
    this.searchTerm.set('');
  }

  showRegisterTenantModal() {
    this.isVisible.update((val) => !val);
  }

  /**
   * Handle resending activation email to tenant admin
   */
  onResendActivationEmail(tenant: CompanyInListDto, event: Event) {
    event.preventDefault();
    event.stopPropagation();

    // Set the tenant ID to track which specific tenant is being processed
    this.resendingTenantId = tenant.id;

    // Use the TanStack Query mutation
    this.resendActivationMutation.mutate(tenant.id);
  }

  /**
   * Handle tenant selection with validation feedback
   */
  onTenantSelect(tenant: CompanyInListDto, event: Event) {
    event.preventDefault(); // Prevent default navigation

    // Map CompanyInListDto to TenantInListDto format
    const tenantData: any = {
      ...tenant,
      address: '', // Admin doesn't require address
      createdAt: new Date(tenant.createdAt as any),
    };

    this.appService.setCurrentTenant(tenantData).subscribe({
      next: (success) => {
        if (success) {
          // Navigate to tenant profile on success using Angular Router
          this.router.navigate(['/tenants/context', tenant.id, 'overview']);
        } else {
          console.error('Failed to set tenant context');
          this.snackBar.open('❌ Failed to set tenant context. Please try again.', 'Close', {
            duration: 5000,
            horizontalPosition: 'end',
            verticalPosition: 'top',
          });
        }
      },
      error: (error) => {
        console.error('Error setting tenant context:', error);
        this.snackBar.open('❌ Error setting tenant context. Please try again.', 'Close', {
          duration: 5000,
          horizontalPosition: 'end',
          verticalPosition: 'top',
        });
      },
    });
  }
}
